import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, RefreshCw, AlertCircle, Plus } from 'lucide-react';
import { meetingsApi } from '../services/api';
import { useAuthStore } from '../store/auth';
import { socketService } from '../services/socket';
import MeetingCard from '../components/meeting/MeetingCard';
import CreateMeetingModal from '../components/meeting/CreateMeetingModal';
import type { Meeting } from '../types';

export default function TodayPage() {
  const { user } = useAuthStore();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch today's meetings
  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['meetings', 'today'],
    queryFn: meetingsApi.getTodayMeetings,
    refetchInterval: 60000,
  });

  // Fetch upcoming meetings (next 7 days)
  const { data: upcomingData = [] } = useQuery({
    queryKey: ['meetings', 'upcoming'],
    queryFn: meetingsApi.getUpcomingMeetings,
    refetchInterval: 300000, // every 5 min
  });

  // Update meetings when data changes
  useEffect(() => {
    if (data) {
      setMeetings(data);
    }
  }, [data]);

  // Listen for real-time updates
  useEffect(() => {
    const handleMeetingUpdate = (meeting: Meeting) => {
      setMeetings((prev) => {
        const index = prev.findIndex((m) => m.id === meeting.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = meeting;
          return updated;
        } else {
          const meetingDate = new Date(meeting.startTime);
          const today = new Date();
          if (
            meetingDate.getDate() === today.getDate() &&
            meetingDate.getMonth() === today.getMonth() &&
            meetingDate.getFullYear() === today.getFullYear()
          ) {
            return [...prev, meeting];
          }
          return prev;
        }
      });
    };

    const handleMeetingDeleted = (meetingId: string) => {
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
    };

    socketService.on('meeting:updated', handleMeetingUpdate);
    socketService.on('meeting:created', handleMeetingUpdate);
    socketService.on('meeting:deleted', handleMeetingDeleted);

    return () => {
      socketService.off('meeting:updated', handleMeetingUpdate);
      socketService.off('meeting:created', handleMeetingUpdate);
      socketService.off('meeting:deleted', handleMeetingDeleted);
    };
  }, []);

  // Filter meetings for current user (operators only see their own)
  const filterByUser = (list: Meeting[]) =>
    user?.role === 'OPERATOR' ? list.filter((m) => m.userId === user.id) : list;

  const sortedMeetings = [...filterByUser(meetings)].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const sortedUpcoming = [...filterByUser(upcomingData)].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  // Group upcoming by day
  const upcomingByDay = sortedUpcoming.reduce<Record<string, Meeting[]>>((acc, meeting) => {
    const dateKey = format(new Date(meeting.startTime), 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(meeting);
    return acc;
  }, {});

  const todayDate = format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 safe-top">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">Hoje</h1>
            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className="p-2 text-gray-500 hover:text-gray-700 active:text-gray-900 tap-target"
            >
              <RefreshCw
                className={`w-5 h-5 ${isRefetching ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
          <p className="text-sm text-gray-500 capitalize">{todayDate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm">Carregando agendamentos...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-1">Erro ao carregar agendamentos</p>
            <p className="text-gray-600 text-sm mb-4">
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 tap-target"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Today's Meetings */}
        {!isLoading && !error && (
          <>
            {sortedMeetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mb-3" />
                <p className="text-gray-900 font-medium mb-1">Nenhum agendamento para hoje</p>
                <p className="text-gray-600 text-sm">Aproveite seu dia!</p>
              </div>
            ) : (
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Hoje ({sortedMeetings.length})
                </h2>
                <div className="space-y-3">
                  {sortedMeetings.map((meeting) => (
                    <MeetingCard key={meeting.id} meeting={meeting} />
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Meetings */}
            {Object.keys(upcomingByDay).length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Próximos atendimentos
                </h2>
                <div className="space-y-4 pb-20">
                  {Object.entries(upcomingByDay).map(([dateKey, dayMeetings]) => (
                    <div key={dateKey}>
                      <p className="text-sm font-medium text-primary-700 mb-2 capitalize">
                        {format(new Date(dateKey + 'T12:00:00'), "EEEE, d 'de' MMMM", { locale: ptBR })}
                      </p>
                      <div className="space-y-2">
                        {dayMeetings.map((meeting) => (
                          <MeetingCard key={meeting.id} meeting={meeting} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* FAB - Floating Action Button */}
      <button
        onClick={() => setIsCreateModalOpen(true)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform tap-target z-40"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultDate={new Date()}
      />
    </div>
  );
}
